import { Component } from '@angular/core';
import { finalize } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RreLink, RreLinkBody } from '../../models/rrelinking.model';
import { RrelinkingService } from '../../services/rrelinking.service';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { openDeleteConfirmationDialog } from '../../shared/utils/confirm-dialog.utils';
import { RreLinkModalComponent } from './rre-link-modal/rre-link-modal.component';
import { BaseTableComponent } from '../../shared/base/base-table.component';

@Component({
  selector: 'app-rrelinking',
  templateUrl: './rrelinking.component.html',
  styleUrl: './rrelinking.component.css',
  standalone: false,
})
export class RrelinkingComponent extends BaseTableComponent<RreLink> {
  readonly displayedColumns: string[] = ['office', 'officeName', 'rre', 'rreName', 'actions'];
  readonly pageSizes = [10, 20, 50];
  override sortBy = 'office';
  override sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private rrelinkingService: RrelinkingService,
    private dialog: MatDialog
  ) {
    super();
  }

  protected override loadData(skip: number, count: number): void {
    this.loading = true;
    this.errorMessage = '';

    this.rrelinkingService
      .getRreLinks(undefined, undefined, skip, count, this.sortBy, this.sortDir, this.searchTerm)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.dataSource.data = result.links;
          this.totalCount = result.totalCount;
        },
        error: (error) => {
          this.errorMessage = formatHttpErrorMessage('Failed to load RRE links', error);
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }

  deleteLink(row: RreLink): void {
    this.errorMessage = '';
    openDeleteConfirmationDialog(
      this.dialog,
      'Delete RRE Link',
      `Are you sure you want to delete the link for Office ${row.OfficeName || row.Office} / RRE ${row.RRE}?`
    ).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      const body: RreLinkBody = {
        claimOfficeID: row.Office,
        officeName: row.OfficeName,
        rre: row.RRE,
        rreName: row.RREName,
      };
      this.loading = true;
      this.rrelinkingService.deleteRreLink(body)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: () => this.loadDataWrapper(),
          error: (error) => {
            this.errorMessage = formatHttpErrorMessage('Failed to delete RRE link', error);
          },
        });
    });
  }

  onAddLink(): void {
    this.errorMessage = '';

    const dialogRef = this.dialog.open(RreLinkModalComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'rre-link-dialog',
      data: {},
    });

    dialogRef.componentInstance.submitRequest.subscribe(result => {
      const body: RreLinkBody = {
        claimOfficeID: result.claimOfficeId,
        officeName: result.officeName,
        rre: result.rre,
        rreName: result.rreName,
      };
      this.loading = true;
      this.rrelinkingService.addRreLink(body)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: () => {
            dialogRef.close();
            this.loadDataWrapper();
          },
          error: (error) => {
            dialogRef.componentInstance.dialogError = formatHttpErrorMessage('Failed to add RRE link', error);
          },
        });
    });
  }
}
